<app-header [title]="project()?.name || 'Project'"
            [description]="project()?.topic || 'Manage your project settings'">

  <button suffix
          mat-raised-button
          type="button"
          (click)="openDatabaseOverview()">
    <mat-icon fontSet="material-symbols-outlined">dictionary</mat-icon>
    <span>Learn more about database</span>
  </button>

</app-header>

@if (projectId() && hasSettings()) {
  <form [formGroup]="form">
    <mat-stepper orientation="vertical" class="stepper">
      <mat-step>
        <ng-template matStepLabel>Overview</ng-template>
        <ng-template matStepContent>
          <div class="db-toggle-container">
            <!-- PubMed Toggle -->
            <div class="toggle-item">
              <div class="toggle-info">
                <h3>PubMed</h3>
                <p>
                  PubMed is a free search engine accessing primarily the MEDLINE database of references and abstracts on
                  life sciences and biomedical topics.
                  Use the toggle to enable or disable PubMed queries.
                </p>
                @if (!pubmedLocalEnabled() && !hasPubMedApiKey) {
                  <app-info-card
                    title="Missing API key"
                    subtitle="An API key would improve your search experience"
                    type="INFO"
                    buttonText="Open settings"
                    (buttonClicked)="openSettings()"
                    [marginTop]="true">
                  </app-info-card>
                }
                @if (pubmedLocalEnabled()) {
                  <app-info-card
                    title="Local PubMed"
                    subtitle="We are using a local PubMed instance, so no API key is required."
                    type="INFO"
                  ></app-info-card>
                }
              </div>
              <mat-slide-toggle formControlName="enablePubMedQuery">Use Pubmed</mat-slide-toggle>
            </div>
            <div class="toggle-item">
              <div class="toggle-info">
                <h3>Scopus</h3>
                <p>
                  Scopus is one of the largest abstract and citation databases of peer-reviewed literature, covering
                  scientific journals, books, and conference proceedings.
                  Enable or disable Scopus queries with this switch.
                </p>
                @if (!hasScopusApiKey) {
                  <app-info-card
                    title="Missing API key"
                    subtitle="An Scopus API key is required. Please set one."
                    type="WARNING"
                    buttonText="Open settings"
                    (buttonClicked)="openSettings()"
                    [marginTop]="true">
                  </app-info-card>
                }
              </div>
              <mat-slide-toggle formControlName="enableScopusQuery">Enable Scopus Query</mat-slide-toggle>
            </div>
            <div class="toggle-item">
              <div class="toggle-info">
                <h3>Semantic Scholar</h3>
                <p>
                  Semantic Scholar is an AI-powered research tool that helps researchers quickly find relevant
                  scientific literature.
                  Toggle this switch to activate or deactivate Semantic Scholar queries.
                </p>
                @if (!hasSemanticScholarApiKey) {
                  <app-info-card
                    title="Missing API key"
                    subtitle="An API key would improve you search experience"
                    type="INFO"
                    buttonText="Open settings"
                    (buttonClicked)="openSettings()"
                    [marginTop]="true">
                  </app-info-card>
                }
              </div>
              <mat-slide-toggle formControlName="enableSemanticScholarQuery">Enable Semantic Scholar Query
              </mat-slide-toggle>
            </div>
          </div>

          <div class="actions">
            <span class="spacer"></span>
            <button mat-button matStepperNext>Next to your research queries</button>
          </div>
        </ng-template>
      </mat-step>
      <mat-step>
        <ng-template matStepLabel>Queries</ng-template>
        <ng-template matStepContent>
          <app-info-card size="SMALL" [marginTop]="true" title="Queries">
            <span subtitle> A database can have several queries. The results from these queries are added together—not
            filtered to show only overlapping results.
            </span>
          </app-info-card>


          <mat-tab-group mat-stretch-tabs="false" mat-align-tabs="start">
            <mat-tab>
              <ng-template mat-tab-label>
                @if (form.get('enablePubMedQuery')?.value) {
                  <mat-icon class="tab-icon">search</mat-icon>
                } @else {
                  <mat-icon class="tab-icon">search_off</mat-icon>
                }
                Pubmed
              </ng-template>
              @if (form.get('enablePubMedQuery')?.value) {
                <div formArrayName="researchPubmedSettings">
                  @for (setting of getResearchSettings(ResearchSource.PUBMED).controls; track $index) {
                    <div [formGroupName]="$index">
                      <app-screening-research-settings-field
                        #researchSettings
                        [projectId]="projectId()!"
                        [screeningSettingsId]="settingId"
                        [source]="ResearchSource.PUBMED"
                        [setting]="getScreeningSettingsResearchDBDTO($index, ResearchSource.PUBMED)"
                        [formGroup]="getResearchSettingGroup($index, ResearchSource.PUBMED)"
                        [enabled]="form.get('enablePubMedQuery')?.value"
                        (remove)="removeResearchSetting($index, ResearchSource.PUBMED, $event)"
                        [keywords]="keywords()">
                      </app-screening-research-settings-field>
                    </div>
                    <mat-divider></mat-divider>
                  }
                  @if (form.get('enablePubMedQuery')?.value) {
                    <button mat-flat-button class="add-btn" type="button"
                            (click)="addResearchSetting(ResearchSource.PUBMED)">
                      Add Pubmed Query
                    </button>
                  }
                </div>
              } @else {
                <app-error-card
                  warningButtonText="You disabled this database"
                >
                  <span>If you would like to search in this database</span>
                  <mat-slide-toggle formControlName="enablePubMedQuery">enable it</mat-slide-toggle>
                </app-error-card>
              }
            </mat-tab>


            <mat-tab>
              <ng-template mat-tab-label>
                @if (form.get('enableScopusQuery')?.value) {
                  <mat-icon class="tab-icon">search</mat-icon>
                } @else {
                  <mat-icon class="tab-icon">search_off</mat-icon>
                }
                Scopus
              </ng-template>
              @if (form.get('enableScopusQuery')?.value) {
                <div formArrayName="researchScopusSettings">
                  @for (setting of getResearchSettings(ResearchSource.SCOPUS).controls; track $index) {
                    <div [formGroupName]="$index">
                      <app-screening-research-settings-field
                        #researchSettings
                        [projectId]="projectId()!"
                        [screeningSettingsId]="settingId"
                        [source]="ResearchSource.SCOPUS"
                        [setting]="getScreeningSettingsResearchDBDTO($index, ResearchSource.SCOPUS)"
                        [formGroup]="getResearchSettingGroup($index, ResearchSource.SCOPUS)"
                        [enabled]="form.get('enableScopusQuery')?.value"
                        [keywords]="keywords()"
                        (remove)="removeResearchSetting($index, ResearchSource.SCOPUS,  $event)">
                      </app-screening-research-settings-field>
                    </div>
                  }
                  @if (form.get('enableScopusQuery')?.value) {
                    <button mat-flat-button class="add-btn" type="button"
                            (click)="addResearchSetting(ResearchSource.SCOPUS)">
                      Add Scopus Query
                    </button>
                  }
                </div>
              } @else {
                <app-error-card
                  warningButtonText="You disabled this database"
                >
                  <span>If you would like to search in this database</span>
                  <mat-slide-toggle formControlName="enableScopusQuery">enable it</mat-slide-toggle>
                </app-error-card>
              }
            </mat-tab>

            <mat-tab>
              <ng-template mat-tab-label>
                @if (form.get('enableSemanticScholarQuery')?.value) {
                  <mat-icon class="tab-icon">search</mat-icon>
                } @else {
                  <mat-icon class="tab-icon">search_off</mat-icon>
                }
                Semantic Scholar
              </ng-template>
              @if (form.get('enableSemanticScholarQuery')?.value) {
                <div formArrayName="researchSemanticSettings">
                  @for (setting of getResearchSettings(ResearchSource.SEMANTIC_SCHOLAR).controls; track $index) {
                    <div [formGroupName]="$index">
                      <app-screening-research-settings-field
                        #researchSettings
                        [projectId]="projectId()!"
                        [screeningSettingsId]="settingId"
                        [source]="ResearchSource.SEMANTIC_SCHOLAR"
                        [setting]="getScreeningSettingsResearchDBDTO($index, ResearchSource.SEMANTIC_SCHOLAR)"
                        [formGroup]="getResearchSettingGroup($index, ResearchSource.SEMANTIC_SCHOLAR)"
                        [enabled]="form.get('enableSemanticScholarQuery')?.value"
                        [keywords]="keywords()"
                        (remove)="removeResearchSetting($index, ResearchSource.SEMANTIC_SCHOLAR, $event)">
                      </app-screening-research-settings-field>
                    </div>
                  }
                  @if (form.get('enableSemanticScholarQuery')?.value) {
                    <button mat-flat-button class="add-btn" type="button"
                            (click)="addResearchSetting(ResearchSource.SEMANTIC_SCHOLAR)">
                      Add Semantic Scholar Query
                    </button>
                  }
                </div>
              } @else {
                <app-error-card
                  warningButtonText="You disabled this database"
                >
                  <span>If you would like to search in this database</span>
                  <mat-slide-toggle formControlName="enableSemanticScholarQuery">enable it</mat-slide-toggle>
                </app-error-card>
              }
            </mat-tab>

          </mat-tab-group>
          <div class="actions">
            <button mat-button matStepperPrevious>Back to overview</button>
            <button mat-button matStepperNext>Next to query meta settings</button>
          </div>
        </ng-template>
      </mat-step>
      <mat-step>
        <ng-template matStepLabel>Metadata</ng-template>

        <div class="form-row">
          <mat-slide-toggle formControlName="enableYearFilter"
                            class="year-filter"
          >Enable date filter
          </mat-slide-toggle>

          <mat-form-field appearance="fill" class="year-input">
            <mat-label>From Year</mat-label>
            <input matInput type="number" formControlName="fromYear" placeholder="Enter start year"/>
            <mat-icon class="info" matSuffix
                      matTooltip="If set, this is the start year for the search.">
              info
            </mat-icon>
            @if (form.get('fromYear')?.hasError('min')) {
              <mat-error>
                From year must be greater than or equal to 1900.
              </mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="fill" class="year-input">
            <mat-label>To Year</mat-label>
            <input matInput type="number" formControlName="toYear" placeholder="Enter end year"/>
            <mat-icon class="info" matSuffix
                      matTooltip="If set, this is the end year for the search.">
              info
            </mat-icon>
            @if (form.get('toYear')?.hasError('min')) {
              <mat-error>
                To year must be greater than or equal to 1900.
              </mat-error>
            }
            @if (form.get('toYear')?.hasError('yearRange')) {
              <mat-error>
                To year must be greater than or equal to From year.
              </mat-error>
            }
          </mat-form-field>
        </div>
        <div class="actions">
          <button mat-button matStepperPrevious>Back to your queries</button>

          @if (editable()) {
            <span class="spacer"></span>
            <app-save-btn class="save-abstract-btn"
                          [element]="setting()"
                          (clicked)="save()"
            ></app-save-btn>
          }
        </div>

        <app-info-card size="SMALL" [marginTop]="true" title="Filter">
        <span subtitle>
        These are fixed filters, if set, that must be fulfilled—for example, publication years
        </span>
        </app-info-card>
      </mat-step>
    </mat-stepper>
  </form>

  <mat-divider></mat-divider>
  <div class="actions">
    <a class="next-btn"
       [routerLink]="['../setting'] "
       mat-raised-button>
      Back to search keywords and criteria
    </a>
    <span class="spacer"></span>
    <a class="next-btn"
       [routerLink]="['../screening'] "
       mat-raised-button>
      To your identified papers
    </a>
  </div>

} @else {
  @if (noKeywords()) {
    <app-empty-state
      title="No keywords found"
      icon="warning"
      subtitle="To use this page, you need to define keywords first.
          Please go to the settings to add the necessary keywords."
    >
    </app-empty-state>
  } @else if (!editable()) {
    <app-empty-state
      title="No search queries yet"
      icon="info"
      subtitle="The owner of this project has not created search queries yet."
    >
    </app-empty-state>
  } @else {

    <app-loading-card
      class="no-settings-card"
      title="We are creating your customized search queries">
      <p>Analyzing your keywords and creating meaningful queries for you ...</p>
    </app-loading-card>
  }
}
